import React from 'react';
import { Button, Typography, Row, Col, Card, Space } from 'antd';
import { 
  GoogleOutlined,
  ClockCircleOutlined,
  CheckSquareOutlined,
  BarChartOutlined,
  RightOutlined,
  StarFilled,
  SafetyCertificateOutlined,
  CloudOutlined,
  MobileOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const { Title, Text, Paragraph } = Typography;

const LandingPage = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: <ClockCircleOutlined style={{ fontSize: '32px', color: '#1E90FF' }} />,
      title: 'Pomodoro Timer',
      description: 'Focus sessions with customizable work and break intervals. Track your productivity with detailed analytics.',
      color: '#1E90FF'
    },
    {
      icon: <CheckSquareOutlined style={{ fontSize: '32px', color: '#52c41a' }} />,
      title: 'Habit Tracker',
      description: 'Build and maintain positive daily habits. Set targets, track progress, and celebrate achievements.',
      color: '#52c41a'
    },
    {
      icon: <BarChartOutlined style={{ fontSize: '32px', color: '#722ed1' }} />,
      title: 'Analytics Dashboard',
      description: 'Comprehensive insights into your productivity patterns. Visualize your progress over time.',
      color: '#722ed1'
    }
  ];

  const benefits = [
    {
      icon: <SafetyCertificateOutlined style={{ fontSize: '24px', color: '#1E90FF' }} />,
      title: 'Secure & Private',
      description: 'Google Firebase authentication with your data encrypted and secure'
    },
    {
      icon: <CloudOutlined style={{ fontSize: '24px', color: '#52c41a' }} />,
      title: 'Cloud Sync',
      description: 'Access your data anywhere, anytime. Seamless sync across all devices'
    },
    {
      icon: <MobileOutlined style={{ fontSize: '24px', color: '#722ed1' }} />,
      title: 'Mobile Friendly',
      description: 'Fully responsive design that works perfectly on phone, tablet, and desktop'
    }
  ];

  const handleGetStarted = () => {
    navigate('/login');
  };

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(180deg, #FFF8F0 0%, #FFFFFF 100%)' }}>
      <Helmet>
        <title>Progress Craft - Your Ultimate Productivity Hub</title>
        <meta name="description" content="Boost your productivity with Progress Craft. Features Pomodoro Timer, Habit Tracker, and detailed analytics. Perfect for students, professionals, and productivity enthusiasts." />
      </Helmet>

      {/* Hero Section */}
      <div style={{ 
        background: 'linear-gradient(135deg, #1E90FF15, #87CEEB20)', 
        padding: '80px 20px',
        textAlign: 'center'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ marginBottom: '32px' }}>
            <div style={{ 
              fontSize: '64px', 
              marginBottom: '16px',
              background: 'linear-gradient(135deg, #1E90FF, #87CEEB)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontWeight: 'bold'
            }}>
              ⚡
            </div>
            <Title level={1} style={{ 
              fontSize: 'clamp(2rem, 5vw, 3.5rem)',
              color: '#000080',
              marginBottom: '16px',
              fontWeight: 'bold'
            }}>
              Progress Craft
            </Title>
            <Title level={2} style={{ 
              fontSize: 'clamp(1.25rem, 3vw, 2rem)',
              color: '#4B4B4B',
              fontWeight: 'normal',
              marginBottom: '24px'
            }}>
              Your Ultimate Productivity Hub
            </Title>
          </div>

          <Paragraph style={{ 
            fontSize: 'clamp(1rem, 2.5vw, 1.25rem)',
            color: '#666',
            marginBottom: '40px',
            maxWidth: '800px',
            margin: '0 auto 40px'
          }}>
            Transform your productivity with our comprehensive suite of tools. 
            Track focus sessions, build lasting habits, and gain insights into your progress. 
            Perfect for students, professionals, and anyone serious about achieving their goals.
          </Paragraph>

          <Space size="large" style={{ marginBottom: '48px' }}>
            <Button 
              type="primary" 
              size="large"
              icon={<GoogleOutlined />}
              onClick={handleGetStarted}
              style={{ 
                backgroundColor: '#1E90FF',
                borderColor: '#1E90FF',
                height: '56px',
                fontSize: '18px',
                fontWeight: 'bold',
                borderRadius: '28px',
                padding: '0 32px'
              }}
            >
              Get Started with Google
            </Button>
            <Button 
              size="large"
              onClick={handleGetStarted}
              style={{ 
                height: '56px',
                fontSize: '18px',
                borderRadius: '28px',
                padding: '0 32px',
                borderColor: '#1E90FF',
                color: '#1E90FF'
              }}
            >
              Learn More
            </Button>
          </Space>

          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px' }}>
            <div style={{ display: 'flex', gap: '2px' }}>
              {[1,2,3,4,5].map(star => (
                <StarFilled key={star} style={{ color: '#faad14', fontSize: '16px' }} />
              ))}
            </div>
            <Text style={{ color: '#666', fontSize: '14px' }}>
              Trusted by 1000+ productive people
            </Text>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div style={{ padding: '80px 20px', maxWidth: '1200px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '64px' }}>
          <Title level={2} style={{ 
            color: '#000080',
            fontSize: 'clamp(1.75rem, 4vw, 2.5rem)',
            marginBottom: '16px'
          }}>
            Everything You Need to Succeed
          </Title>
          <Text style={{ 
            fontSize: 'clamp(1rem, 2.5vw, 1.125rem)',
            color: '#666',
            display: 'block'
          }}>
            Powerful tools designed to help you build better habits and maximize your productivity
          </Text>
        </div>

        <Row gutter={[32, 32]}>
          {features.map((feature, index) => (
            <Col xs={24} md={8} key={index}>
              <Card 
                style={{ 
                  height: '100%',
                  borderRadius: '16px',
                  border: `2px solid ${feature.color}20`,
                  boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                  transition: 'all 0.3s ease'
                }}
                bodyStyle={{ padding: '32px 24px', textAlign: 'center' }}
                hoverable
              >
                <div style={{ marginBottom: '20px' }}>
                  {feature.icon}
                </div>
                <Title level={4} style={{ 
                  color: feature.color,
                  marginBottom: '16px',
                  fontSize: '1.25rem'
                }}>
                  {feature.title}
                </Title>
                <Text style={{ 
                  color: '#666',
                  fontSize: '1rem',
                  lineHeight: '1.6'
                }}>
                  {feature.description}
                </Text>
              </Card>
            </Col>
          ))}
        </Row>
      </div>

      {/* Benefits Section */}
      <div style={{ 
        background: 'linear-gradient(135deg, #f8f9fa, #e9ecef)', 
        padding: '80px 20px' 
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', textAlign: 'center' }}>
          <Title level={2} style={{ 
            color: '#000080',
            fontSize: 'clamp(1.75rem, 4vw, 2.5rem)',
            marginBottom: '48px'
          }}>
            Why Choose Progress Craft?
          </Title>

          <Row gutter={[32, 32]}>
            {benefits.map((benefit, index) => (
              <Col xs={24} sm={8} key={index}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ marginBottom: '16px' }}>
                    {benefit.icon}
                  </div>
                  <Title level={4} style={{ 
                    color: '#000080',
                    marginBottom: '12px',
                    fontSize: '1.125rem'
                  }}>
                    {benefit.title}
                  </Title>
                  <Text style={{ 
                    color: '#666',
                    fontSize: '1rem',
                    lineHeight: '1.5'
                  }}>
                    {benefit.description}
                  </Text>
                </div>
              </Col>
            ))}
          </Row>
        </div>
      </div>

      {/* CTA Section */}
      <div style={{ 
        padding: '80px 20px',
        textAlign: 'center',
        background: 'linear-gradient(135deg, #1E90FF10, #87CEEB10)'
      }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <Title level={2} style={{ 
            color: '#000080',
            fontSize: 'clamp(1.75rem, 4vw, 2.5rem)',
            marginBottom: '24px'
          }}>
            Ready to Transform Your Productivity?
          </Title>
          <Paragraph style={{ 
            fontSize: 'clamp(1rem, 2.5vw, 1.125rem)',
            color: '#666',
            marginBottom: '40px'
          }}>
            Join thousands of users who have already boosted their productivity with Progress Craft. 
            Start your journey today - it's free and takes less than 30 seconds to get started.
          </Paragraph>

          <Button 
            type="primary" 
            size="large"
            icon={<GoogleOutlined />}
            onClick={handleGetStarted}
            style={{ 
              backgroundColor: '#1E90FF',
              borderColor: '#1E90FF',
              height: '56px',
              fontSize: '18px',
              fontWeight: 'bold',
              borderRadius: '28px',
              padding: '0 40px'
            }}
          >
            Start Your Productivity Journey
            <RightOutlined style={{ marginLeft: '8px' }} />
          </Button>

          <div style={{ marginTop: '24px' }}>
            <Text style={{ color: '#999', fontSize: '14px' }}>
              ✅ Free to use • ✅ No credit card required • ✅ Google sign-in
            </Text>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div style={{ 
        background: '#f8f9fa',
        padding: '40px 20px',
        textAlign: 'center',
        borderTop: '1px solid #e9ecef'
      }}>
        <Text style={{ color: '#666' }}>
          © 2024 Progress Craft. Built with ❤️ for productivity enthusiasts.
        </Text>
      </div>
    </div>
  );
};

export default LandingPage; 